import React, {useEffect, useState} from "react";
import {Button, Card, Col, Container, Row, Modal} from "react-bootstrap";
import Select, {SingleValue} from "react-select";
import {UserBasic} from "../../../service/types/types";
import {useParams} from "react-router-dom";
import {UserBasicService} from "../../../service/users-basic.service";
import UpdateUserBasic from "./updateUserBasic";
import { FeedbackModal } from "../../common/feedback.modal";

export default function ViewUserBasic() {
  const [selectedUser, setSelectedUser] = useState<UserBasic | null>(null);
  const [users, setUsers] = useState<UserBasic[]>([]);
  const [usersOptions, setUsersOptions] = useState<{ value: string; label: string }[]>([]);
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUserSelected, setIsUserSelected] = useState(false);
  const [isUserRequested, setIsUserRequested] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState<"success" | "error">("success");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if(!isUserRequested || isUpdated) {
      const fetchUsers = async () => {
        try {
          const data = await UserBasicService.findAll();
          setUsers(data);
          const options = data.map((user) => ({
            value: user.rut,
            label: `${user.nombre} (${user.rut})`,
          }));
          setUsersOptions(options);
          setIsUserRequested(true);
          setIsUpdated(false);

        } catch (error) {
          alert("Error obteniendo trabajadores");
          console.error("Error fetching users:", error);
        }
      };
      fetchUsers();
    }
  }, [isUpdated]);

  useEffect(() => {
    if (id && users.length > 0 && !isUserSelected) {
      handleSelect({ value: id, label: "" });
      setIsUserSelected(true)
    }
    setShowUpdate(false);
  }, [id, users]);

  const handleSelect = (option: SingleValue<{ value: string; label: string }>) => {
    if (!option) {
      setSelectedUser(null);
      setShowUpdate(false);
      return;
    }
    const user = users.find((u) => u.rut === option.value) || null;
    if (user) {
      console.log("Selected user:", user);
      const selected: UserBasic = {
        rut: user.rut,
        nombre: user.nombre,
        cargo: user.cargo,
        correo: user.correo,
        zona: user.zona,
      };
      setSelectedUser(selected);
      setShowUpdate(false);
    }
  };

  const renderField = (label: string, value?: any) => (
    <Col md={4} className="mb-3">
      <div className="p-3 border rounded bg-light">
        <strong className="d-block text-secondary">{label}</strong>
        <span className="fs-6">{value}</span>
      </div>
    </Col>
  );

  const refreshUser = async () => {
    setShowUpdate(false);
    setIsUserSelected(false);
    setSelectedUser(null);
    setIsUpdated(true);
  }

  const handleDelete = async () => {
    if (!selectedUser) return;
    setIsDeleting(true);
    try {
      await UserBasicService.remove(selectedUser.rut);
      setFeedbackType("success");
      setFeedbackMessage("Usuario eliminado correctamente.");
      setFeedbackModal(true);
      setIsUpdated(true);
      await refreshUser()
    } catch (error) {
      setFeedbackType("error");
      setFeedbackMessage("Error eliminando usuario.");
      setFeedbackModal(true);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="w-100 min-vh-100 bg-light d-flex justify-content-center p-3">
      <Container fluid>
        <h2 className="mb-4 text-center">Información de Usuarios</h2>

        {/* Buscador */}
        <Card className="shadow-sm mb-4 p-3">
          <Select
            options={usersOptions}
            onChange={handleSelect}
            placeholder="Buscar usuario por nombre o RUT..."
            isSearchable
            className="w-100"
          />
        </Card>

        {/* Información del usuario seleccionado */}
        
        {selectedUser && !showUpdate && (
          <Card className="shadow-sm p-4">
            <Row>
              {renderField("RUT", selectedUser.rut)}
              {renderField("Nombre", selectedUser.nombre)}
              {renderField("Correo", selectedUser.correo)}
              {renderField("Cargo", selectedUser.cargo)}
              {renderField("Zona", selectedUser.zona)}
            </Row>
            <div className="mt-4 text-center">
              <Button variant="warning" onClick={() => setShowUpdate(true)} className="w-100 mt-3">
                Editar Usuario
              </Button>
              <Button variant="danger" onClick={() => setShowDeleteModal(true)} className="w-100 mt-3">
                Eliminar Usuario
              </Button>
            </div>
          </Card>
        )}

        {/* Componente de edición */}
        {selectedUser && showUpdate && (
          <UpdateUserBasic user={selectedUser} refreshUser={refreshUser} />
        )}

        {/* Modal de confirmación de eliminación */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Estás seguro de que deseas eliminar a {selectedUser?.nombre}? Esta acción no se puede deshacer.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de feedback */}
        <FeedbackModal
          show={feedbackModal}
          onHide={() => setFeedbackModal(false)}
          type={feedbackType}
          message={feedbackMessage}
        />
      </Container>
    </div>
  );
}